import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  InputLabel,
  Chip,
  CircularProgress,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Category as CategoryIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as PencilIcon,
  BoltOutlined as BoltIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Memory as MemoryIcon,
} from "@mui/icons-material";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import {
  ReadOnlyDataGrid,
  type ReadOnlyField,
  type RowAction,
} from "../components/ReadOnlyDataGrid";
import { CategorySelectDialog } from "../components/CategorySelectDialog";
import {
  getUploadSessions,
  getSessionDocuments,
  streamUploadSessionStatus,
  deleteUploadSession,
  retrySessionDocument,
  deleteSessionDocument,
  getKbDocuments,
  getGoldenStandards,
  deleteGoldenStandard,
  updateKbDocumentMetadata,
  deleteKbDocument,
  downloadKbDocument,
  awakenExtractor,
  getServerRam,
  type UploadSessionItem,
  type SessionStatusPayload,
  type UploadDocumentItem,
  type KbFileItem,
  type RamStatsResponse,
} from "../services/api";
import type { GoldenStandardItem } from "../types";
import { useCategories } from "../hooks/useCategories";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  MODEL_UNAVAILABLE_CODE,
  MODEL_UNAVAILABLE_MESSAGE,
  notifyModelUnavailable,
} from "../utils/llmAvailability";

const DOC_FETCH_LIMIT = 5000;

export const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const sessionsRowRef = React.useRef<HTMLDivElement | null>(null);
  const [uploadSessions, setUploadSessions] = React.useState<
    UploadSessionItem[]
  >([]);
  const [sessionStatusMap, setSessionStatusMap] = React.useState<
    Record<string, SessionStatusPayload>
  >({});
  const [uploadSessionModal, setUploadSessionModal] = React.useState<{
    open: boolean;
    session: UploadSessionItem | null;
    documents: UploadDocumentItem[];
    documentsLoading?: boolean;
  }>({ open: false, session: null, documents: [] });
  const [kbDocuments, setKbDocuments] = React.useState<KbFileItem[]>([]);
  const [goldenStandards, setGoldenStandards] = React.useState<
    GoldenStandardItem[]
  >([]);
  const [sessionsLoading, setSessionsLoading] = React.useState(true);
  const [docsLoading, setDocsLoading] = React.useState(true);
  const [gsLoading, setGsLoading] = React.useState(true);
  const { data: categories = [] } = useCategories();

  const [docTotal, setDocTotal] = React.useState(0);

  // Estrattore (miner) awake status
  type ExtractorStatus = "idle" | "checking" | "awake" | "sleeping";
  const [extractorStatus, setExtractorStatus] =
    React.useState<ExtractorStatus>("idle");
  const extractorCheckingRef = React.useRef(false);

  // RAM stats
  const [ramStats, setRamStats] = React.useState<RamStatsResponse | null>(
    null
  );

  const fetchRam = React.useCallback(async () => {
    try {
      const stats = await getServerRam();
      setRamStats(stats);
    } catch {
      // silenzioso, non critico
    }
  }, []);

  const checkExtractor = React.useCallback(async () => {
    if (extractorCheckingRef.current) return;
    extractorCheckingRef.current = true;
    setExtractorStatus("checking");
    try {
      const result = await awakenExtractor();
      setExtractorStatus(result.awake ? "awake" : "sleeping");
    } catch {
      setExtractorStatus("sleeping");
    } finally {
      extractorCheckingRef.current = false;
    }
  }, []);

  const loadAllDocs = React.useCallback(async () => {
    setDocsLoading(true);
    try {
      const res = await getKbDocuments({ skip: 0, limit: DOC_FETCH_LIMIT });
      const items = Array.isArray(res?.items) ? res.items : [];
      setKbDocuments(items);
      setDocTotal(res?.total ?? items.length);
    } catch (err) {
      console.error("Error loading documents:", err);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const refreshDocs = loadAllDocs;

  const [docMenuAnchor, setDocMenuAnchor] = React.useState<{
    element: HTMLElement;
    fileId: string;
  } | null>(null);
  const [gsMenuAnchor, setGsMenuAnchor] = React.useState<{
    element: HTMLElement;
    fileId: string;
  } | null>(null);
  const [editCategoryModal, setEditCategoryModal] = React.useState<{
    open: boolean;
    fileId: string;
    currentCategory: string;
    /** Se presenti, il modale mostra anche titolo e data (apertura da menu ... tabella) */
    title?: string;
    publicationDate?: string | null;
  }>({ open: false, fileId: "", currentCategory: "" });
  const [deleteDocModal, setDeleteDocModal] = React.useState<{
    open: boolean;
    fileId: string;
    fileName: string;
  }>({ open: false, fileId: "", fileName: "" });
  const [deleteGsModal, setDeleteGsModal] = React.useState<{
    open: boolean;
    chunkId: number;
    title: string;
  }>({ open: false, chunkId: 0, title: "" });

  // Carica RAM al mount
  React.useEffect(() => {
    void fetchRam();
  }, [fetchRam]);

  // Carica le tre sezioni in parallelo ma con stati di loading indipendenti
  React.useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      setSessionsLoading(true);
      try {
        const res = await getUploadSessions();
        if (!cancelled) {
          setUploadSessions(Array.isArray(res) ? res : []);
        }
      } catch (error) {
        console.error("Error loading upload sessions:", error);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    };

    const loadDocuments = async () => {
      if (!cancelled) await loadAllDocs();
    };

    const loadGoldenStandards = async () => {
      setGsLoading(true);
      try {
        const res = await getGoldenStandards({ skip: 0, limit: 500 });
        if (!cancelled) {
          setGoldenStandards(Array.isArray(res?.items) ? res.items : []);
        }
      } catch (error) {
        console.error("Error loading golden standards:", error);
      } finally {
        if (!cancelled) setGsLoading(false);
      }
    };

    void loadSessions();
    void loadDocuments();
    void loadGoldenStandards();

    return () => {
      cancelled = true;
    };
  }, [loadAllDocs]);

  // Trigger automatico controllo estrattore quando ci sono sessioni in corso
  React.useEffect(() => {
    if (uploadSessions.some((s) => s.status === "open")) {
      void checkExtractor();
    }
  }, [uploadSessions, checkExtractor]);

  // SSE per sessioni aperte; quando una sessione finisce (completed/failed) ricarica sessioni e documenti
  React.useEffect(() => {
    const aborts: (() => void)[] = [];
    uploadSessions.forEach((s) => {
      if (s.status === "open") {
        const abort = streamUploadSessionStatus(
          s.id,
          (data) => {
            setSessionStatusMap((prev) => ({ ...prev, [s.id]: data }));
            if (data.files?.length) {
              setUploadSessionModal((prev) => {
                if (
                  prev.session?.id !== data.session_id ||
                  prev.documents.length === 0
                )
                  return prev;
                const merged = prev.documents.map((doc) => {
                  const fromSse = data.files!.find((f) => f.id === doc.id);
                  if (!fromSse) return doc;
                  return {
                    ...doc,
                    status: fromSse.status,
                    progress: fromSse.progress,
                    step_label: fromSse.step_label ?? doc.step_label,
                    failure_code: fromSse.failure_code ?? doc.failure_code,
                    failure_message: fromSse.failure_message ?? doc.failure_message,
                  };
                });
                return { ...prev, documents: merged };
              });
            }
            if (data.status === "completed" || data.status === "failed") {
              if (
                data.files?.some(
                  (file) => file.failure_code === MODEL_UNAVAILABLE_CODE
                )
              ) {
                notifyModelUnavailable();
              }
              Promise.all([
                getUploadSessions().catch(() => []),
                getSessionDocuments(data.session_id).catch(() => []),
                getKbDocuments({ skip: 0, limit: DOC_FETCH_LIMIT }).catch(
                  () => ({
                    items: [],
                    total: 0,
                  })
                ),
              ]).then(([sessions, docs, kbDocsRes]) => {
                setUploadSessions(sessions);
                const docsItems = Array.isArray(kbDocsRes?.items)
                  ? kbDocsRes.items
                  : [];
                setKbDocuments(docsItems);
                setDocTotal(kbDocsRes?.total ?? 0);

                setUploadSessionModal((prev) => {
                  if (prev.session?.id !== data.session_id) return prev;
                  const updatedSession = sessions.find(
                    (s) => s.id === data.session_id
                  );
                  return {
                    ...prev,
                    documents: docs,
                    session: updatedSession ?? prev.session,
                  };
                });
              });
            }
          },
          () => {}
        );
        aborts.push(abort);
      }
    });
    return () => aborts.forEach((a) => a());
  }, [uploadSessions]);

  // Carica documenti quando si apre la modale sessione upload
  React.useEffect(() => {
    if (!uploadSessionModal.open || !uploadSessionModal.session?.id) return;
    getSessionDocuments(uploadSessionModal.session.id).then((docs) =>
      setUploadSessionModal((prev) => ({
        ...prev,
        documents: docs,
        documentsLoading: false,
      }))
    );
  }, [uploadSessionModal.open, uploadSessionModal.session?.id]);

  const refreshSessionModalData = React.useCallback(
    async (sessionId: string) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const list = await getUploadSessions();
      setUploadSessions(list);
      const session = list.find((s) => s.id === sessionId);
      const docs = await getSessionDocuments(sessionId);
      setUploadSessionModal((prev) =>
        prev.session?.id === sessionId
          ? {
              ...prev,
              documents: docs,
              session: session ?? prev.session,
            }
          : prev
      );
    },
    []
  );

  const handleRetryDocument = async (documentId: string) => {
    const sessionId = uploadSessionModal.session?.id;
    try {
      await retrySessionDocument(documentId);
      toast.success("Riprova avviata");
      if (sessionId) {
        await refreshSessionModalData(sessionId);
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Errore riprova documento"
      );
    }
  };

  const handleDeleteSessionDocument = async (
    documentId: string,
    documentTitle: string
  ) => {
    const sessionId = uploadSessionModal.session?.id;
    try {
      await deleteSessionDocument(documentId);
      toast.success("Documento rimosso dalla sessione");
      if (sessionId) {
        await refreshSessionModalData(sessionId);
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Errore rimozione documento"
      );
    }
  };

  const handleDocMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    fileId: string
  ) => {
    setDocMenuAnchor({ element: event.currentTarget, fileId });
  };
  const handleDocMenuClose = () => setDocMenuAnchor(null);
  const handleGsMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    fileId: string
  ) => {
    setGsMenuAnchor({ element: event.currentTarget, fileId });
  };
  const handleGsMenuClose = () => setGsMenuAnchor(null);

  const handleEditCategory = (
    fileId: string,
    currentCategory: string,
    options?: { title: string; publicationDate: string | null }
  ) => {
    setEditCategoryModal({
      open: true,
      fileId,
      currentCategory,
      title: options?.title,
      publicationDate: options?.publicationDate,
    });
    handleDocMenuClose();
  };
  const handleDeleteDocument = (fileId: string, fileName: string) => {
    setDeleteDocModal({ open: true, fileId, fileName });
    handleDocMenuClose();
  };

  const handleConfirmEditCategory = async (
    selectedValue: string,
    extra?: { title?: string; publicationDate?: string | null }
  ) => {
    const { fileId } = editCategoryModal;
    if (!fileId) return;
    try {
      const payload: {
        category: string;
        file_title?: string;
        datetime_?: string;
      } = {
        category: selectedValue,
      };
      if (extra?.title !== undefined) payload.file_title = extra.title;
      if (extra?.publicationDate !== undefined)
        payload.datetime_ = extra.publicationDate ?? undefined;
      await updateKbDocumentMetadata(fileId, payload);
      toast.success("Metadati aggiornati");
      await refreshDocs();
      setEditCategoryModal({ open: false, fileId: "", currentCategory: "" });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail ?? "Errore aggiornamento metadati"
      );
    }
  };

  const handleConfirmDeleteDocument = async () => {
    const { fileId } = deleteDocModal;
    if (!fileId) return;
    try {
      await deleteKbDocument(fileId);
      toast.success("Documento eliminato dalla Knowledge Base");
      await refreshDocs();
      setDeleteDocModal({ open: false, fileId: "", fileName: "" });
    } catch (err: unknown) {
      toast.error(
        (err as any)?.response?.data?.detail ??
          (err as any)?.detail ??
          "Errore eliminazione"
      );
    }
  };

  const handleConfirmDeleteGoldenStandard = async () => {
    const { chunkId } = deleteGsModal;
    if (!chunkId) return;
    try {
      await deleteGoldenStandard(chunkId);
      toast.success("Golden Standard eliminato");
      const gsRes = await getGoldenStandards();
      setGoldenStandards(gsRes?.items ?? []);
      setDeleteGsModal({ open: false, chunkId: 0, title: "" });
    } catch (err: unknown) {
      toast.error(
        (err as any)?.response?.data?.detail ??
          (err as any)?.detail ??
          "Errore eliminazione"
      );
    }
  };

  // Documenti: solo type document da /documents; Golden Standard: da getGoldenStandards()
  const regularDocuments = kbDocuments.filter((d) => d.type === "document");

  const scrollSessionsRow = (direction: "left" | "right") => {
    if (!sessionsRowRef.current) return;
    const offset = direction === "left" ? -340 : 340;
    sessionsRowRef.current.scrollBy({ left: offset, behavior: "smooth" });
  };

  // Fields per ReadOnlyDataGrid - Documenti
  const documentFields: ReadOnlyField[] = React.useMemo(
    () => [
      {
        field: "icon",
        headerName: "",
        width: 50,
        renderCell: () => (
          <PdfIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        ),
      },
      {
        field: "name",
        headerName: "Documento",
        width: "auto",
      },
      {
        field: "category",
        headerName: "Categoria",
        width: "auto",
      },
      {
        field: "publicationDate",
        headerName: "Data Pubblicazione",
        width: "auto",
        type: "date",
      },
      {
        field: "loadingDate",
        headerName: "Data Caricamento",
        width: "auto",
        type: "date",
      },
      {
        field: "actions",
        headerName: "",
        width: 60,
        align: "right",
        headerAlign: "right",
        sticky: "right",
        renderCell: (params: any) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDocMenuOpen(e, params.row.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
    ],
    []
  );

  // Preparare i dati per ReadOnlyDataGrid - Documenti (da embedding/files)
  const documentGridData = React.useMemo(() => {
    return regularDocuments.map((doc) => ({
      id: doc.file_id,
      name: doc.file_title || doc.hosted_file_name || doc.file_id,
      category:
        categories.find((c) => c.value === (doc.category ?? ""))?.label ??
        doc.category ??
        "",
      publicationDate: doc.datetime_ ?? null,
      loadingDate: doc.updated_at ?? doc.created_at ?? null,
    }));
  }, [regularDocuments, categories]);

  // Fields per ReadOnlyDataGrid - Golden Standard (stesse colonne di Documenti, icona diversa)
  const goldenStandardFields: ReadOnlyField[] = React.useMemo(
    () => [
      {
        field: "icon",
        headerName: "",
        width: 50,
        renderCell: () => (
          <CollectionsBookmarkIcon
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
        ),
      },
      {
        field: "name",
        headerName: "Documento",
        width: "auto",
      },
      {
        field: "category",
        headerName: "Categoria",
        width: "auto",
      },
      {
        field: "publicationDate",
        headerName: "Data Pubblicazione",
        width: "auto",
        type: "date",
      },
      {
        field: "loadingDate",
        headerName: "Data Caricamento",
        width: "auto",
        type: "date",
      },
      {
        field: "actions",
        headerName: "",
        width: 60,
        align: "right",
        headerAlign: "right",
        sticky: "right",
        renderCell: (params: any) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleGsMenuOpen(e, params.row.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
    ],
    []
  );

  // Preparare i dati per ReadOnlyDataGrid - Golden Standard (da getGoldenStandards)
  const goldenStandardGridData = React.useMemo(() => {
    return goldenStandards.map((item) => ({
      id: String(item.chunk_id),
      name: item.title,
      category:
        categories.find((c) => c.value === (item.category ?? ""))?.label ??
        item.category ??
        "",
      publicationDate: item.date ?? null,
      loadingDate: item.created_at ?? null,
    }));
  }, [goldenStandards, categories]);

  // Fields per ReadOnlyDataGrid - Documenti Sessione (nella modale)
  const sessionDocumentFields: ReadOnlyField[] = React.useMemo(
    () => [
      {
        field: "name",
        headerName: "Documento",
        width: 250,
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params: any) => {
          const status = params.value;
          const progress = params.row.progress ?? 0;
          const pct = Math.round(Number(progress));
          const statusLabelIt: Record<string, string> = {
            pending: "In attesa",
            uploading: "Caricamento",
            completed: "Completato",
            failed: "Fallito",
            deleted: "Eliminato",
            cancelled: "Annullato",
          };
          const baseLabel =
            statusLabelIt[String(status).toLowerCase()] ?? status;
          const label =
            status === "uploading" ? `${baseLabel} ${pct}%` : baseLabel;
          return (
            <Chip
              label={label}
              size="small"
              color={
                status === "completed"
                  ? "success"
                  : status === "uploading" || status === "pending"
                    ? "primary"
                    : "error"
              }
            />
          );
        },
      },
      {
        field: "step_label",
        headerName: "Fase",
        width: 280,
        renderCell: (params: any) => {
          if (params.row.failure_code === MODEL_UNAVAILABLE_CODE) {
            return (
              <Typography variant="body2" color="warning.main">
                {params.value || MODEL_UNAVAILABLE_MESSAGE}
              </Typography>
            );
          }
          return params.value;
        },
      },
      {
        field: "actions",
        headerName: "Azioni",
        width: 100,
        align: "right",
        headerAlign: "right",
        renderCell: (params: any) => {
          const isSessionCancelled =
            uploadSessionModal.session?.status === "cancelled";
          if (isSessionCancelled) return null;

          const rowStatus = String(params.row.status || "").toLowerCase();
          const isRowFailed = rowStatus === "failed" || rowStatus === "deleted";
          const isRowDeletable =
            rowStatus === "uploading" || rowStatus === "pending";
          return (
            <>
              {isRowFailed ? (
                <Tooltip title="Riprova" enterDelay={300}>
                  <IconButton
                    size="small"
                    onClick={() => handleRetryDocument(params.row.id)}
                    aria-label="Riprova"
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {isRowDeletable ? (
                <Tooltip title="Elimina" enterDelay={300}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleDeleteSessionDocument(
                        params.row.id,
                        params.row.name
                      )
                    }
                    aria-label="Elimina"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          );
        },
      },
    ],
    [
      handleDeleteSessionDocument,
      handleRetryDocument,
      uploadSessionModal.session?.status,
    ]
  );

  const sessionDocumentGridData = React.useMemo(() => {
    return uploadSessionModal.documents.map((doc) => ({
      id: doc.id,
      name: doc.title,
      status: doc.status,
      progress: doc.progress ?? 0,
      step_label: doc.step_label ?? "—",
      failure_code: doc.failure_code ?? null,
      failure_message: doc.failure_message ?? null,
    }));
  }, [uploadSessionModal.documents]);

  const sessionHasModelUnavailableFailure = React.useMemo(
    () =>
      uploadSessionModal.documents.some(
        (doc) => doc.failure_code === MODEL_UNAVAILABLE_CODE
      ),
    [uploadSessionModal.documents]
  );

  // Get current section (most recent or in progress) - non più usato, mostriamo tutte le sezioni

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "#F5F5F5",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minHeight: 0,
      }}
    >
      {/* Sessioni di Caricamento - Top Full Width */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="h6">Sessioni di Caricamento</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {sessionsLoading && <CircularProgress size={18} />}
            {/* RAM stats */}
            {ramStats && (
              <Tooltip
                title={`RAM totale: ${ramStats.total_gb} GB — Usata: ${ramStats.used_gb} GB — Disponibile: ${ramStats.available_gb} GB`}
                arrow
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "default",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    bgcolor:
                      ramStats.percent_used >= 85
                        ? "error.50"
                        : ramStats.percent_used >= 65
                        ? "warning.50"
                        : "success.50",
                  }}
                >
                  <MemoryIcon
                    sx={{
                      fontSize: 15,
                      color:
                        ramStats.percent_used >= 85
                          ? "error.main"
                          : ramStats.percent_used >= 65
                          ? "warning.main"
                          : "success.main",
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      color:
                        ramStats.percent_used >= 85
                          ? "error.main"
                          : ramStats.percent_used >= 65
                          ? "warning.main"
                          : "success.main",
                      lineHeight: 1,
                    }}
                  >
                    RAM {ramStats.percent_used}%
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", lineHeight: 1 }}
                  >
                    ({ramStats.available_gb} GB liberi)
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Bottone Sveglia Estrattore */}
            <Tooltip
              title={
                extractorStatus === "awake"
                  ? "L'estrattore è attivo e pronto"
                  : extractorStatus === "checking"
                  ? "Verifica in corso..."
                  : extractorStatus === "sleeping"
                  ? "L'estrattore non ha risposto. Riprova."
                  : "Clicca per svegliare e verificare lo stato dell'estrattore"
              }
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant={extractorStatus === "awake" ? "contained" : "outlined"}
                  color={
                    extractorStatus === "awake"
                      ? "success"
                      : extractorStatus === "sleeping"
                      ? "error"
                      : "primary"
                  }
                  disabled={extractorStatus === "checking"}
                  onClick={checkExtractor}
                  startIcon={
                    extractorStatus === "checking" ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : extractorStatus === "awake" ? (
                      <CheckCircleOutlineIcon fontSize="small" />
                    ) : (
                      <BoltIcon fontSize="small" />
                    )
                  }
                  sx={{ textTransform: "none", whiteSpace: "nowrap", fontSize: 12 }}
                >
                  {extractorStatus === "awake"
                    ? "Estrattore Pronto"
                    : extractorStatus === "checking"
                    ? "Verifica..."
                    : extractorStatus === "sleeping"
                    ? "Risveglia Estrattore"
                    : "Sveglia Estrattore"}
                </Button>
              </span>
            </Tooltip>

            {/* Scroll buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label="Scorri sessioni a sinistra"
                onClick={() => scrollSessionsRow("left")}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Scorri sessioni a destra"
                onClick={() => scrollSessionsRow("right")}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Box
          ref={sessionsRowRef}
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 2,
            overflowX: "auto",
            overflowY: "hidden",
            pb: 0.4,
            px: 0.1,
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {uploadSessions.map((session) => {
            const statusData = sessionStatusMap[session.id];
            // Preferisci lo stato dalla lista (API) se è terminale, così dopo delete/refetch si vede failed/completed
            const terminalStatus = session.status as string | undefined;
            const isTerminal =
              terminalStatus === "completed" ||
              terminalStatus === "failed" ||
              terminalStatus === "cancelled";
            const status = isTerminal
              ? terminalStatus
              : (statusData?.status ?? session.status ?? "open");
            const progress =
              statusData?.total_progress ?? session.total_progress ?? 0;
            const progressPct = Math.round(Number(progress ?? 0));
            const statusConfig: Record<
              string,
              {
                label: string;
                color:
                  | "default"
                  | "primary"
                  | "secondary"
                  | "error"
                  | "info"
                  | "success"
                  | "warning";
              }
            > = {
              open: { label: "Uploading", color: "primary" },
              completed: { label: "Completata", color: "success" },
              failed: { label: "Fallita", color: "error" },
              cancelled: { label: "Annullata", color: "default" },
            };
            const { label: statusLabel, color: statusColor } = statusConfig[
              String(status)
            ] ?? { label: String(status), color: "default" as const };
            return (
              <Paper
                key={session.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: "pointer",
                  minWidth: 300,
                  maxWidth: 300,
                  flex: "0 0 300px",
                  "&:hover": { boxShadow: 4 },
                  border: "1px solid #E0E0E0",
                }}
                onClick={() =>
                  setUploadSessionModal({
                    open: true,
                    session,
                    documents: [],
                    documentsLoading: true,
                  })
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Sessione:{" "}
                    {new Date(session.created_at).toLocaleDateString("it-IT")}
                  </Typography>
                  <Chip label={statusLabel} color={statusColor} size="small" />
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Documenti:{" "}
                  {statusData?.files?.length ?? session.document_count ?? 0}
                </Typography>
                {status !== "failed" && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={progressPct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mt: 5,
                        bgcolor: "#E0E0E0",
                        "& .MuiLinearProgress-bar": { bgcolor: "#00BCD4" },
                      }}
                    />
                    {statusData?.current_step_label && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {statusData.current_step_label}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Bottom Section: Documenti and Golden Standard - altezza fissa, griglie scrollabili */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 3,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Documenti - Bottom Left */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6">Documenti</Typography>
              {docTotal > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({regularDocuments.length})
                </Typography>
              )}
            </Box>
            <Button
              variant="text"
              size="medium"
              color="inherit"
              startIcon={<AddIcon />}
              onClick={() => navigate("/knowledge-base/upload")}
            >
              Nuova sessione di caricamento
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReadOnlyDataGrid
              fields={documentFields}
              data={documentGridData}
              height="100%"
              getId={(row) => row.id}
              showFooter={false}
              initialPageSize={100}
              loading={docsLoading}
              initialSortModel={[{ field: "loadingDate", sort: "desc" }]}
            />
          </Box>
        </Paper>

        {/* Golden Standard - Bottom Right */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6">Golden Standard</Typography>
            <Button
              variant="text"
              size="medium"
              color="inherit"
              startIcon={<AddIcon />}
              onClick={() => navigate("/knowledge-base/golden")}
            >
              Nuovo Golden Standard
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ReadOnlyDataGrid
              fields={goldenStandardFields}
              data={goldenStandardGridData}
              height="100%"
              getId={(row) => row.id}
              showFooter={false}
              loading={gsLoading}
            />
          </Box>
        </Paper>
      </Box>

      {/* Menu Documenti (grid Documenti) */}
      <Menu
        anchorEl={docMenuAnchor?.element}
        open={!!docMenuAnchor}
        onClose={handleDocMenuClose}
      >
        {docMenuAnchor && (
          <>
            <MenuItem
              onClick={() => {
                const doc = kbDocuments.find(
                  (d) => d.file_id === docMenuAnchor.fileId
                );
                if (doc) {
                  handleDocMenuClose();
                  downloadKbDocument(
                    doc.file_id,
                    doc.file_title || doc.hosted_file_name
                  ).catch(() => toast.error("Errore durante il download"));
                }
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Scarica file</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                const doc = kbDocuments.find(
                  (d) => d.file_id === docMenuAnchor.fileId
                );
                if (doc)
                  handleEditCategory(doc.file_id, doc.category ?? "", {
                    title: doc.file_title ?? "",
                    publicationDate: doc.datetime_ ?? null,
                  });
              }}
            >
              <ListItemIcon>
                <PencilIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Modifica</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                const doc = kbDocuments.find(
                  (d) => d.file_id === docMenuAnchor.fileId
                );
                if (doc)
                  handleDeleteDocument(
                    doc.file_id,
                    doc.file_title || doc.file_id
                  );
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Elimina</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Menu Golden Standard (grid Golden Standard, da getGoldenStandards) */}
      <Menu
        anchorEl={gsMenuAnchor?.element}
        open={!!gsMenuAnchor}
        onClose={handleGsMenuClose}
      >
        {gsMenuAnchor && (
          <>
            <MenuItem
              onClick={() => {
                handleGsMenuClose();
                navigate(`/knowledge-base/golden/${gsMenuAnchor.fileId}`);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Modifica</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                const gsItem = goldenStandards.find(
                  (g) => String(g.chunk_id) === gsMenuAnchor.fileId
                );
                if (gsItem) {
                  setDeleteGsModal({
                    open: true,
                    chunkId: gsItem.chunk_id,
                    title: gsItem.title,
                  });
                  handleGsMenuClose();
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Elimina</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Modale Modifica Categoria (da menu ... tabella: permette anche titolo e data) */}
      <CategorySelectDialog
        open={editCategoryModal.open}
        onClose={() =>
          setEditCategoryModal({
            open: false,
            fileId: "",
            currentCategory: "",
            title: undefined,
            publicationDate: undefined,
          })
        }
        value={editCategoryModal.currentCategory}
        onConfirm={handleConfirmEditCategory}
        title={
          editCategoryModal.title !== undefined
            ? "Modifica documento"
            : "Modifica Categoria"
        }
        initialTitle={editCategoryModal.title}
        initialPublicationDate={editCategoryModal.publicationDate}
      />

      {/* Modale Conferma Eliminazione Documento */}
      <Dialog
        open={deleteDocModal.open}
        onClose={() =>
          setDeleteDocModal({ open: false, fileId: "", fileName: "" })
        }
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Eliminare il file &quot;{deleteDocModal.fileName}&quot; dalla
            Knowledge Base?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDocModal({ open: false, fileId: "", fileName: "" })
            }
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirmDeleteDocument}
            color="error"
            variant="contained"
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale Conferma Eliminazione Golden Standard */}
      <Dialog
        open={deleteGsModal.open}
        onClose={() => setDeleteGsModal({ open: false, chunkId: 0, title: "" })}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Eliminare il Golden Standard &quot;{deleteGsModal.title}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteGsModal({ open: false, chunkId: 0, title: "" })
            }
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirmDeleteGoldenStandard}
            color="error"
            variant="contained"
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale Dettagli Sessione Upload */}
      <Dialog
        open={uploadSessionModal.open}
        onClose={() =>
          setUploadSessionModal({
            open: false,
            session: null,
            documents: [],
            documentsLoading: false,
          })
        }
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: 4 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="span">
            Dettagli Sessione
            {uploadSessionModal.session?.id
              ? `: ${uploadSessionModal.session.id.slice(0, 8)}...`
              : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() =>
                setUploadSessionModal({
                  open: false,
                  session: null,
                  documents: [],
                  documentsLoading: false,
                })
              }
              size="small"
              sx={{ ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Documenti
          </Typography>
          {sessionHasModelUnavailableFailure && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {MODEL_UNAVAILABLE_MESSAGE} Uno o più documenti non sono stati
              indicizzati. Puoi riprovare con il pulsante &quot;Riprova&quot;.
            </Alert>
          )}
          {uploadSessionModal.documentsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ReadOnlyDataGrid
              fields={sessionDocumentFields}
              data={sessionDocumentGridData}
              height={400}
              getId={(row) => row.id}
              showFooter={false}
            />
          )}

          <Button
            variant="contained"
            color="error"
            size="medium"
            sx={{ width: "100%", mt: 2 }}
            disabled={uploadSessionModal.session?.status !== "open"}
            onClick={async () => {
              const sessionId = uploadSessionModal.session?.id;
              if (!sessionId) return;
              try {
                await deleteUploadSession(sessionId);
                const list = await getUploadSessions();
                setUploadSessions(list);
                setSessionStatusMap((prev) => {
                  const next = { ...prev };
                  delete next[sessionId];
                  return next;
                });
                setUploadSessionModal({
                  open: false,
                  session: null,
                  documents: [],
                  documentsLoading: false,
                });
                toast.success("Sessione annullata");
              } catch (err: unknown) {
                toast.error(
                  (err as { response?: { data?: { detail?: string } } })
                    ?.response?.data?.detail ?? "Errore eliminazione sessione"
                );
              }
            }}
          >
            Cancella sezione
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
